import React, { useMemo, useState } from 'react';
import { Divider, List, Typography } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { CartItemDisplay } from '@eshop/types/categories.types.ts';

interface OrderSummaryAccordionProps {
    items: CartItemDisplay[];
    totalLabel?: string; // Kept for backward compatibility but not displayed
    totalValue: string;
    itemCount: number;
    originalTotalValue?: string;
    children?: React.ReactNode;
    style?: React.CSSProperties;
    className?: string;
}

export const OrderSummaryAccordion: React.FC<OrderSummaryAccordionProps> = ({
    items,
    // totalLabel not used - kept for API compatibility
    totalValue,
    itemCount,
    originalTotalValue,
    children,
    style,
    className,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const summaryLabel = useMemo(() => {
        if (itemCount === 1) {
            return '1 item';
        }
        return `${itemCount} items`;
    }, [itemCount]);

    return (
        <div className={`checkout-order-accordion ${className ?? ''}`} style={style}>
            <div
                className="checkout-order-accordion__trigger"
                onClick={() => setIsOpen((prev) => !prev)}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
            >
                <div className="checkout-order-accordion__header">
                    <Typography.Text className="checkout-order-accordion__items">
                        {summaryLabel}
                    </Typography.Text>
                    <div className="checkout-order-accordion__totals">
                        {originalTotalValue && (
                            <Typography.Text delete className="checkout-order-accordion__original-total">
                                {originalTotalValue}
                            </Typography.Text>
                        )}
                        <Typography.Text strong className="checkout-order-accordion__final-total">
                            {totalValue}
                        </Typography.Text>
                    </div>
                    <span className="checkout-order-accordion__icon">
                        {isOpen ? <UpOutlined /> : <DownOutlined />}
                    </span>
                </div>
            </div>
            {isOpen ? (
                <>
                    <Divider className="checkout-order-accordion__divider" />
                    <List
                        itemLayout="horizontal"
                        dataSource={items}
                        renderItem={(item) => (
                            <List.Item key={item.key}>
                                <List.Item.Meta
                                    avatar={item.imageElement}
                                    title={item.title}
                                    description={item.description}
                                />
                            </List.Item>
                        )}
                    />
                    {children && (
                        <div className="checkout-order-accordion__summary">
                            <Divider className="checkout-order-accordion__divider" />
                            {children}
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};
